"use client";

import { useState } from "react";
import { Icon, Text } from "@pamperedchef/design-system";
import type { CatalogLabel } from "@/domain/catalog";
import * as styles from "./ProductOfferLabel.module.scss";

export interface ProductOfferLabelProps {
	/**
	 * `kind: "product_offer"` labels from `CatalogProduct.labels` +
	 * `scheduledLabels` (date-window filtered upstream). Callers filter
	 * by kind; this composite stays presentational.
	 */
	labels: CatalogLabel[];
	/**
	 * When more than `collapseAfter` entries are passed, the overflow
	 * collapses behind a "+N More Deals" toggle. Default: 2.
	 */
	collapseAfter?: number;
	className?: string;
}

/**
 * Stacks the product's free-shipping / host-party / discount messages
 * below the price block. Each row: leading icon, label text, themed
 * background. Three offers or more → first two shown, remainder behind
 * a "+N More Deals" expand affordance.
 */
export function ProductOfferLabel({
	labels,
	collapseAfter = 2,
	className
}: ProductOfferLabelProps) {
	const [expanded, setExpanded] = useState(false);

	if (!labels.length) return null;

	const collapsible = labels.length > collapseAfter;
	const visible =
		!collapsible || expanded ? labels : labels.slice(0, collapseAfter);
	const hiddenCount = labels.length - collapseAfter;

	return (
		<div
			className={[styles["offer-label"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="ProductOfferLabel"
		>
			{visible.map((label, idx) => (
				<div
					key={`${label.label}-${idx}`}
					className={[
						styles["offer-label__row"],
						styles[`offer-label__row--${label.theme ?? "default"}`]
					].join(" ")}
				>
					{label.icon ? (
						<Icon
							name={label.icon}
							width={20}
							height={20}
							color="#73296D"
						/>
					) : null}
					<Text
						tag="p"
						styledAs="mdr"
						className={styles["offer-label__text"]}
					>
						{label.label}
					</Text>
				</div>
			))}
			{collapsible && !expanded ? (
				<button
					type="button"
					className={styles["offer-label__toggle"]}
					onClick={() => setExpanded(true)}
				>
					{`+${hiddenCount} More Deals`}
				</button>
			) : null}
		</div>
	);
}
